import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiStatsBar,
} from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ServiceItemType {
  id: number
  name: string
}

interface Row {
  user_id: number
  name: string
  cells: Record<number, string> // service_item_type_id -> "HH:MM"
  total_minutes: number
}

interface Employee {
  id: number
  name: string
}

interface Props {
  rows: Row[]
  service_item_types: ServiceItemType[]
  column_totals: Record<number, string>
  start_date: string
  end_date: string
  employees: Employee[]
  all_service_item_types: ServiceItemType[]
  selected_employee_id: number | null
  selected_service_item_type_id: number | null
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, "0")}`
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Service Items", href: "/accounting/reports/service_items" },
]

export default function ServiceItemsReport({
  rows,
  service_item_types,
  column_totals,
  start_date,
  end_date,
  employees,
  all_service_item_types,
  selected_employee_id,
  selected_service_item_type_id,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [employeeId, setEmployeeId] = useState<string>(
    selected_employee_id?.toString() ?? "",
  )
  const [sitId, setSitId] = useState<string>(
    selected_service_item_type_id?.toString() ?? "",
  )

  const handleUpdate = () => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    if (sitId) params.service_item_type_id = sitId
    router.get("/accounting/reports/service_items", params, {
      preserveState: true,
    })
  }

  const grandTotal = rows.reduce((sum, r) => sum + r.total_minutes, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Service Items Report" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            options={employees.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="All Employees"
          />
          <HpiFilterSelect
            label="Service Item"
            value={sitId}
            onChange={setSitId}
            options={all_service_item_types.map((t) => ({
              value: t.id,
              label: t.name,
            }))}
            placeholder="All Types"
          />
        </HpiDateFilterBar>

        <HpiStatsBar
          stats={[
            { label: "Employees", subLabel: "Shown", value: rows.length },
            {
              label: "Service Types",
              subLabel: "Active",
              value: service_item_types.length,
            },
            {
              label: "Total Time",
              subLabel: "HH:MM",
              value: formatMinutes(grandTotal),
            },
          ]}
          className="mb-6"
        />

        {/* Cross-tab Matrix */}
        <div className="border-hpi-light-gray overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-hpi-light-gray border-b bg-gray-50">
                <th className="text-hpi-dark-gray sticky left-0 bg-gray-50 px-4 py-3 text-left font-semibold">
                  Employee
                </th>
                {service_item_types.map((sit) => (
                  <th
                    key={sit.id}
                    className="text-hpi-dark-gray px-3 py-3 text-right font-semibold whitespace-nowrap"
                  >
                    {sit.name}
                  </th>
                ))}
                <th className="text-hpi-dark-gray px-4 py-3 text-right font-bold">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.user_id}
                  className="border-hpi-light-gray border-b hover:bg-gray-50"
                >
                  <td className="text-hpi-dark-gray sticky left-0 bg-white px-4 py-2 font-medium">
                    {row.name}
                  </td>
                  {service_item_types.map((sit) => (
                    <td key={sit.id} className="px-3 py-2 text-right">
                      {row.cells[sit.id] ?? "-"}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right font-medium">
                    {formatMinutes(row.total_minutes)}
                  </td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-hpi-dark-gray border-t-2 bg-gray-50 font-bold">
                  <td className="text-hpi-dark-gray sticky left-0 bg-gray-50 px-4 py-2">
                    TOTAL
                  </td>
                  {service_item_types.map((sit) => (
                    <td key={sit.id} className="px-3 py-2 text-right">
                      {column_totals[sit.id] ?? "-"}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right">
                    {formatMinutes(grandTotal)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No service item data found for the selected criteria.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
